import React from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import OrbLoader from "../common/OrbLoader";
import {
  ProposalVersionChange,
  useGetProposalVersionChangesQuery,
} from "../../store/apis/commercialProposalApi";

/**
 * Qué cambió respecto a la versión anterior.
 *
 * Existe porque una versión nueva no se entiende sola: la V2 se emite porque algo cambió,
 * y hasta ahora la única forma de saber qué era abrir las dos fichas en dos pestañas y
 * compararlas a ojo.
 *
 * El cálculo lo hace el backend y no esta pantalla: hay que comparar dos propuestas
 * enteras con sus proyectos y sus escenarios, y eso es lógica que merece tests, no un
 * `useMemo`.
 */

/** Los campos que el backend puede devolver. El código es estable; la etiqueta, no. */
const FIELD_LABEL: Record<string, string> = {
  clientEngagement: "Tipo de cliente",
  responsableComercial: "Responsable comercial",
  responsablePropuesta: "Responsable de propuesta",
  entryDate: "Fecha de entrada",
  deliveryDeadline: "Fecha límite de entrega",
  estimatedActivationDate: "Activación estimada",
  estimatedDurationMonths: "Duración estimada (meses)",
  totalBudget: "Importe total",
  project: "Línea de proyecto",
  "project.lineName": "Nombre de la línea",
  "project.house": "House",
  "project.budgetType": "Tipo de presupuesto",
  "project.amount": "Importe de la línea",
  "project.contentTags": "Tags de contenido",
  "project.scenarios": "Escenarios",
};

interface Props {
  proposalId: string;
  version: number;
}

const ProposalVersionChanges: React.FC<Props> = ({ proposalId, version }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Una V1 no tiene con qué compararse: no se pregunta.
  const { data, isFetching, isError } = useGetProposalVersionChangesQuery(
    proposalId,
    { skip: version <= 1 }
  );

  if (version <= 1) return null;

  const diff = data?.data;

  const label = (change: ProposalVersionChange) =>
    t(
      `commercialProposals.changes.field.${change.field}`,
      FIELD_LABEL[change.field] ?? change.field
    );

  const kindChip = (kind: ProposalVersionChange["kind"]) => {
    if (kind === "ANADIDO") {
      return (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={t("commercialProposals.changes.added", "añadido")}
        />
      );
    }
    if (kind === "QUITADO") {
      return (
        <Chip
          size="small"
          color="error"
          variant="outlined"
          label={t("commercialProposals.changes.removed", "quitado")}
        />
      );
    }
    return null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        gap={1}
        mb={2}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <HistoryIcon fontSize="small" color="action" />
          <Typography variant="subtitle1">
            {diff?.previous
              ? t(
                  "commercialProposals.changes.title",
                  "Cambios respecto a la V{{version}}",
                  { version: diff.previous.version }
                )
              : t(
                  "commercialProposals.changes.titlePlain",
                  "Cambios respecto a la versión anterior"
                )}
          </Typography>
        </Stack>
        {diff?.previous && (
          <Button
            size="small"
            onClick={() =>
              navigate(`/commercial-proposals/${diff.previous?.id}`)
            }
          >
            {t(
              "commercialProposals.changes.openPrevious",
              "Ver la V{{version}}",
              {
                version: diff.previous.version,
              }
            )}
          </Button>
        )}
      </Stack>

      {isFetching && (
        <Box textAlign="center" py={2}>
          <OrbLoader size="medium" />
        </Box>
      )}

      {isError && (
        <Typography variant="body2" color="text.secondary">
          {t(
            "commercialProposals.changes.failed",
            "No se pudieron calcular los cambios."
          )}
        </Typography>
      )}

      {!isFetching && !isError && diff && diff.changes.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {/* Decirlo es información, no un hueco: significa que la versión se emitió sin
              tocar nada de lo que se compara. */}
          {diff.previous
            ? t(
                "commercialProposals.changes.none",
                "Ninguno: esta versión tiene los mismos datos que la V{{version}}.",
                { version: diff.previous.version }
              )
            : t(
                "commercialProposals.changes.noPrevious",
                "No hay una versión anterior con la que comparar."
              )}
        </Typography>
      )}

      {!isFetching && diff && diff.changes.length > 0 && (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("commercialProposals.changes.what", "Qué")}
                </TableCell>
                <TableCell>
                  {t("commercialProposals.changes.before", "Antes")}
                </TableCell>
                <TableCell>
                  {t("commercialProposals.changes.after", "Ahora")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {diff.changes.map((change, index) => (
                <TableRow key={`${change.field}-${change.line ?? ""}-${index}`}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                      <Typography variant="body2">{label(change)}</Typography>
                      {kindChip(change.kind)}
                    </Stack>
                    {change.line && (
                      <Typography variant="caption" color="text.secondary">
                        {change.line}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {change.before ?? "—"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {change.after ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
};

export default ProposalVersionChanges;
